import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, FileDown, ExternalLink, Filter, Download } from "lucide-react";

const STATUS_LABELS = {
    active: "Ativa",
    expired: "Expirada",
    cancelled: "Cancelada",
    pending: "Pendente",
};

export default function AdminInvoicesPage() {
    const [filters, setFilters] = useState({
        status: "",
        plan: "",
        invoiced: "",
        date_from: "",
        date_to: "",
    });
    const [companies, setCompanies] = useState([]);
    const [companyId, setCompanyId] = useState("");
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [retrying, setRetrying] = useState({});

    useEffect(() => {
        api.get("/admin/companies").then((r) => setCompanies(r.data || [])).catch(() => setCompanies([]));
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.plan) params.plan = filters.plan;
            if (filters.invoiced === "yes") params.invoiced = true;
            if (filters.invoiced === "no") params.invoiced = false;
            if (filters.date_from) params.date_from = filters.date_from;
            if (filters.date_to) params.date_to = filters.date_to;
            if (companyId) params.company_id = companyId;
            const r = await api.get("/admin/subscriptions", { params });
            setSubs(r.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const reissue = async (id) => {
        setRetrying((p) => ({ ...p, [id]: true }));
        try {
            const r = await api.post(`/admin/subscriptions/${id}/issue-invoice`);
            toast.success(r.data?.skipped ? "Já emitida" : `Fatura #${r.data?.document_id || ""} emitida`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRetrying((p) => ({ ...p, [id]: false }));
        }
    };

    const stats = useMemo(() => {
        const total = subs.reduce((acc, s) => acc + (Number(s.amount) || 0), 0);
        const invoiced = subs.filter((s) => s.moloni_invoice_id).length;
        const pendingInvoice = subs.filter((s) => s.status === "active" && !s.moloni_invoice_id).length;
        return { count: subs.length, total, invoiced, pendingInvoice };
    }, [subs]);

    return (
        <div className="space-y-8" data-testid="admin-invoices-page">
            <div>
                <Link to="/app/super" className="label-caps inline-flex items-center gap-2" data-testid="back-to-super">
                    <ArrowLeft size={12} /> Super Admin
                </Link>
                <h1 className="font-display text-5xl text-[#0d1628] mt-4">Faturas emitidas</h1>
                <p className="text-[#52525b] mt-3 max-w-3xl">
                    Histórico global de subscrições e respetivos documentos Moloni (Fatura + Recibo).
                    Pode forçar a reemissão de qualquer subscrição paga sem fatura.
                </p>
            </div>

            <div className="grid sm:grid-cols-4 gap-4">
                <Stat label="Subscrições" value={stats.count} />
                <Stat label="Faturadas" value={stats.invoiced} />
                <Stat label="Por faturar" value={stats.pendingInvoice} accent={stats.pendingInvoice > 0} />
                <Stat label="Receita filtrada" value={`€${stats.total.toFixed(2)}`} />
            </div>

            <div className="card-flat" data-testid="filters">
                <div className="flex items-center gap-2 mb-4">
                    <Filter size={14} className="text-[#b89b6f]" />
                    <div className="label-caps">Filtros</div>
                </div>
                <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    <FilterField label="Empresa">
                        <select className="input-clean" value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="filter-company">
                            <option value="">Todas</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </FilterField>
                    <FilterField label="Estado">
                        <select className="input-clean" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
                            <option value="">Todos</option>
                            <option value="active">Ativa</option>
                            <option value="expired">Expirada</option>
                            <option value="cancelled">Cancelada</option>
                            <option value="pending">Pendente</option>
                        </select>
                    </FilterField>
                    <FilterField label="Plano">
                        <input className="input-clean" placeholder="ex: starter_1m" value={filters.plan} onChange={(e) => setFilters({ ...filters, plan: e.target.value })} data-testid="filter-plan" />
                    </FilterField>
                    <FilterField label="Faturada">
                        <select className="input-clean" value={filters.invoiced} onChange={(e) => setFilters({ ...filters, invoiced: e.target.value })} data-testid="filter-invoiced">
                            <option value="">Todas</option>
                            <option value="yes">Sim</option>
                            <option value="no">Não</option>
                        </select>
                    </FilterField>
                    <FilterField label="Desde">
                        <input className="input-clean" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" />
                    </FilterField>
                    <FilterField label="Até">
                        <input className="input-clean" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" />
                    </FilterField>
                </div>
                <div className="mt-5 flex flex-wrap justify-between gap-3">
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const params = new URLSearchParams();
                                if (filters.status) params.set("status", filters.status);
                                if (filters.plan) params.set("plan", filters.plan);
                                if (filters.invoiced === "yes") params.set("invoiced", "true");
                                if (filters.invoiced === "no") params.set("invoiced", "false");
                                if (filters.date_from) params.set("date_from", filters.date_from);
                                if (filters.date_to) params.set("date_to", filters.date_to);
                                if (companyId) params.set("company_id", companyId);
                                const r = await api.get(`/admin/subscriptions/export.csv?${params.toString()}`, { responseType: "blob" });
                                const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `ihtp-subscricoes-${new Date().toISOString().slice(0, 10)}.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                            } catch (err) {
                                toast.error(formatApiError(err));
                            }
                        }}
                        className="btn-outline"
                        data-testid="export-csv-btn"
                    >
                        <Download size={14} /> Exportar CSV
                    </button>
                    <button type="button" onClick={load} disabled={loading} className="btn-primary" data-testid="apply-filters-btn">
                        <RefreshCw size={14} /> {loading ? "A carregar…" : "Aplicar filtros"}
                    </button>
                </div>
            </div>

            <div className="border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm" data-testid="invoices-table">
                    <thead>
                        <tr className="border-b border-[#e4e4e7] bg-[#f5f3ee]">
                            <th className="text-left p-4 label-caps">Data</th>
                            <th className="text-left p-4 label-caps">Empresa</th>
                            <th className="text-left p-4 label-caps">NIF</th>
                            <th className="text-left p-4 label-caps">Plano</th>
                            <th className="text-left p-4 label-caps">Valor</th>
                            <th className="text-left p-4 label-caps">Estado</th>
                            <th className="text-left p-4 label-caps">Documentos</th>
                            <th className="text-right p-4 label-caps">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subs.length === 0 ? (
                            <tr><td className="p-6 text-center text-[#52525b]" colSpan={8} data-testid="empty-state">Sem subscrições para os filtros aplicados.</td></tr>
                        ) : subs.map((s) => (
                            <tr key={s.id} className="border-b border-[#e4e4e7] last:border-0" data-testid={`row-${s.id}`}>
                                <td className="p-4 text-[#52525b] whitespace-nowrap">{(s.purchased_at || s.starts_at || "").slice(0, 10)}</td>
                                <td className="p-4 text-[#0d1628]">{s.company_name || "—"}</td>
                                <td className="p-4 text-[#52525b]">{s.company_vat || "—"}</td>
                                <td className="p-4 text-[#52525b]">{s.plan}</td>
                                <td className="p-4 text-[#0d1628]">€{Number(s.amount || 0).toFixed(2)}</td>
                                <td className="p-4">
                                    <span className={`text-xs px-2 py-0.5 ${s.status === "active" ? "bg-[#ecfdf5] text-[#065f46]" : "bg-[#f4f4f5] text-[#52525b]"}`}>
                                        {STATUS_LABELS[s.status] || s.status}
                                    </span>
                                </td>
                                <td className="p-4">
                                    <div className="flex flex-col gap-1 text-xs">
                                        {s.moloni_invoice_pdf ? (
                                            <a href={s.moloni_invoice_pdf} target="_blank" rel="noreferrer" className="link-underline inline-flex items-center gap-1" data-testid={`fat-${s.id}`}>
                                                <FileDown size={11} /> Fatura #{s.moloni_invoice_id}
                                            </a>
                                        ) : s.moloni_invoice_id ? (
                                            <span>FT #{s.moloni_invoice_id}</span>
                                        ) : (
                                            <span className="text-[#b45309]">— sem fatura —</span>
                                        )}
                                        {s.moloni_receipt_pdf ? (
                                            <a href={s.moloni_receipt_pdf} target="_blank" rel="noreferrer" className="link-underline inline-flex items-center gap-1" data-testid={`rec-${s.id}`}>
                                                <FileDown size={11} /> Recibo #{s.moloni_receipt_id}
                                            </a>
                                        ) : s.moloni_receipt_id ? (
                                            <span>RC #{s.moloni_receipt_id}</span>
                                        ) : null}
                                    </div>
                                </td>
                                <td className="p-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <Link to={`/app/super/companies/${s.company_id}`} className="btn-ghost text-xs" data-testid={`open-${s.id}`}>
                                            <ExternalLink size={11} /> Ver
                                        </Link>
                                        <button
                                            type="button"
                                            disabled={retrying[s.id] || (s.moloni_invoice_id && s.moloni_receipt_id)}
                                            onClick={() => reissue(s.id)}
                                            className="btn-outline text-xs"
                                            data-testid={`reissue-${s.id}`}
                                        >
                                            <RefreshCw size={11} className={retrying[s.id] ? "animate-spin" : ""} />
                                            {s.moloni_invoice_id && s.moloni_receipt_id ? "Emitida" : retrying[s.id] ? "A emitir…" : "Emitir"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className={`border ${accent ? "border-[#b89b6f]" : "border-[#e4e4e7]"} p-4`}>
            <div className="label-caps">{label}</div>
            <div className={`mt-2 font-display text-3xl ${accent ? "text-[#b89b6f]" : "text-[#0d1628]"}`}>{value}</div>
        </div>
    );
}

function FilterField({ label, children }) {
    return (
        <div>
            <label className="label-caps block mb-2">{label}</label>
            {children}
        </div>
    );
}
